import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { NotificationService } from './notification.service';
import { UserService } from './user.service';
import { BounceBackUtilsService, BounceBackOrderTracking } from './bounceback-utils.service';

export interface BounceBackCoupon {
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
  isValid: boolean;
  message?: string;
  expiryDate?: string;
  minimumOrderAmount?: number;
}

export interface BounceBackBalance {
  availableBalance: number;
  totalEarned: number;
  totalRedeemed: number;
  expiryDate?: string;
}

export interface BounceBackHistory {
  orderId: string;
  orderDate: string;
  pointsEarned: number;
  pointsRedeemed: number;
  description: string;
  type: 'earned' | 'redeemed' | 'expired';
}

export interface CalculateOrderWithCouponRequest {
  couponCode: string;
  customerId: number;
  storeId: number;
  cartItems: any[];
  subtotal: number;
  shippingTotal: number;
  taxTotal: number;
}

export interface CalculateOrderWithCouponResponse {
  isValid: boolean;
  discountAmount: number;
  finalTotal: number;
  message?: string;
  couponDetails?: BounceBackCoupon;
}

@Injectable({
  providedIn: 'root'
})
export class BounceBackService {
  private readonly apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private userService: UserService,
    private bounceBackUtils: BounceBackUtilsService
  ) {}

  /**
   * Validate BounceBack coupon code
   */
  validateCoupon(couponCode: string, customerId: number, storeId: number): Observable<BounceBackCoupon> {
    const request = {
      couponCode: couponCode.trim(),
      customerId: customerId,
      storeId: storeId
    };

    // TODO: Replace with actual API endpoint when available
    // For now, simulate API call with dummy data
    return this.simulateCouponValidation(request);
  }

  /**
   * Calculate order with BounceBack coupon applied
   */
  calculateOrderWithCoupon(request: CalculateOrderWithCouponRequest): Observable<CalculateOrderWithCouponResponse> {
    // TODO: Replace with actual API endpoint when available
    // For now, simulate API call with dummy data
    return this.simulateCalculateOrderWithCoupon(request);
  }

  /**
   * Get customer's BounceBack balance
   */
  getBounceBackBalance(customerId: number): Observable<BounceBackBalance> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    // TODO: Replace with actual API endpoint when available
    // For now, simulate API call with dummy data
    return this.simulateGetBalance(customerId);
  }

  /**
   * Get customer's BounceBack history
   */
  getBounceBackHistory(customerId: number): Observable<BounceBackHistory[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    // TODO: Replace with actual API endpoint when available
    // For now, simulate API call with dummy data
    return this.simulateGetHistory(customerId);
  }

  /**
   * Apply BounceBack points to order
   */
  applyBounceBackPoints(customerId: number, pointsToUse: number, orderTotal: number): Observable<any> {
    const request = {
      customerId: customerId,
      pointsToUse: pointsToUse,
      orderTotal: orderTotal
    };

    // TODO: Replace with actual API endpoint when available
    // For now, simulate API call with dummy data
    return this.simulateApplyPoints(request);
  }

  // Private methods for simulation (to be replaced with actual API calls)

  private simulateCouponValidation(request: any): Observable<BounceBackCoupon> {
    return of(null).pipe(
      map(() => {
        // Simulate API delay
        const delay = Math.random() * 1000 + 500;
        
        // Simulate different coupon scenarios
        const couponCode = request.couponCode.toLowerCase();
        
        if (couponCode.includes('bounce') || couponCode.includes('back')) {
          if (couponCode.includes('expired')) {
            return {
              code: request.couponCode,
              discount: 0,
              type: 'percentage' as const,
              isValid: false,
              message: 'This BounceBack coupon has expired.',
              expiryDate: '2023-12-31'
            };
          } else if (couponCode.includes('invalid')) {
            return {
              code: request.couponCode,
              discount: 0,
              type: 'percentage' as const,
              isValid: false,
              message: 'Invalid BounceBack coupon code.'
            };
          } else if (couponCode.includes('minimum')) {
            return {
              code: request.couponCode,
              discount: 15,
              type: 'percentage' as const,
              isValid: false,
              message: 'Minimum order amount of $50 required for this coupon.',
              minimumOrderAmount: 50
            };
          } else {
            // Valid BounceBack coupon
            const discount = couponCode.includes('20') ? 20 : 
                           couponCode.includes('15') ? 15 : 
                           couponCode.includes('10') ? 10 : 5;
            
            return {
              code: request.couponCode,
              discount: discount,
              type: 'percentage' as const,
              isValid: true,
              message: `Valid BounceBack coupon! ${discount}% discount applied.`,
              expiryDate: '2024-12-31'
            };
          }
        } else {
          return {
            code: request.couponCode,
            discount: 0,
            type: 'percentage' as const,
            isValid: false,
            message: 'Invalid coupon code. Please enter a valid BounceBack coupon.'
          };
        }
      }),
      catchError(error => {
        this.notificationService.error('Error', 'Failed to validate coupon. Please try again.');
        return of({
          code: request.couponCode,
          discount: 0,
          type: 'percentage' as const,
          isValid: false,
          message: 'Network error. Please try again.'
        });
      })
    );
  }

  private simulateCalculateOrderWithCoupon(request: CalculateOrderWithCouponRequest): Observable<CalculateOrderWithCouponResponse> {
    return of(null).pipe(
      map(() => {
        const couponCode = request.couponCode.toLowerCase();
        
        if (couponCode.includes('bounce') || couponCode.includes('back')) {
          const discount = couponCode.includes('20') ? 20 : 
                          couponCode.includes('15') ? 15 : 
                          couponCode.includes('10') ? 10 : 5;
          
          const discountAmount = (request.subtotal * discount) / 100;
          const finalTotal = request.subtotal - discountAmount + request.shippingTotal + request.taxTotal;
          
          return {
            isValid: true,
            discountAmount: discountAmount,
            finalTotal: finalTotal,
            message: `BounceBack coupon applied! ${discount}% discount: $${discountAmount.toFixed(2)}`,
            couponDetails: {
              code: request.couponCode,
              discount: discount,
              type: 'percentage' as const,
              isValid: true
            }
          };
        } else {
          return {
            isValid: false,
            discountAmount: 0,
            finalTotal: request.subtotal + request.shippingTotal + request.taxTotal,
            message: 'Invalid coupon code.'
          };
        }
      }),
      catchError(error => {
        this.notificationService.error('Error', 'Failed to calculate order with coupon. Please try again.');
        return of({
          isValid: false,
          discountAmount: 0,
          finalTotal: request.subtotal + request.shippingTotal + request.taxTotal,
          message: 'Network error. Please try again.'
        });
      })
    );
  }

  private simulateGetBalance(customerId: number): Observable<BounceBackBalance> {
    return of({
      availableBalance: 125.50,
      totalEarned: 450.75,
      totalRedeemed: 325.25,
      expiryDate: '2024-12-31'
    });
  }

  private simulateGetHistory(customerId: number): Observable<BounceBackHistory[]> {
    return of([
      {
        orderId: 'ORD-2024-001',
        orderDate: '2024-01-15',
        pointsEarned: 25.50,
        pointsRedeemed: 0,
        description: 'Order #ORD-2024-001 - 15% BounceBack earned',
        type: 'earned'
      },
      {
        orderId: 'ORD-2024-002',
        orderDate: '2024-01-20',
        pointsEarned: 0,
        pointsRedeemed: 50.00,
        description: 'BounceBack points redeemed for discount',
        type: 'redeemed'
      },
      {
        orderId: 'ORD-2023-150',
        orderDate: '2023-12-15',
        pointsEarned: 30.25,
        pointsRedeemed: 0,
        description: 'Order #ORD-2023-150 - 15% BounceBack earned',
        type: 'earned'
      }
    ]);
  }

  private simulateApplyPoints(request: any): Observable<any> {
    return of({
      success: true,
      pointsUsed: request.pointsToUse,
      discountApplied: request.pointsToUse,
      remainingBalance: 75.50
    });
  }

  /**
   * Credit rewards after successful autoship (Frontend hook for backend integration)
   * This will be called by the backend automation system, not directly from frontend
   */
  creditAutoshipReward(customerId: number, autoshipOrderId: string, orderTotal: number): Observable<any> {
    // Log the autoship completion for tracking
    this.bounceBackUtils.logBounceBackTransaction('reward_earned', {
      action: 'autoship_completed',
      customerId,
      autoshipOrderId,
      orderTotal,
      note: 'Reward crediting handled by backend automation system'
    }, customerId);

    // The actual reward crediting is handled by the backend automation system
    // This is just a logging hook for frontend tracking
    return of({
      success: true,
      message: 'Autoship completed. Rewards will be credited by the backend system.',
      note: 'Reward calculation and crediting handled by Paymenture API and backend automation'
    });
  }

  /**
   * Calculate BBC bonus for order items (for commission tracking only)
   */
  calculateBBCBonusForOrder(items: any[], couponPercentage: number): number {
    return this.bounceBackUtils.calculateBBCBonus(items, couponPercentage);
  }

  /**
   * Create order tracking data for commission calculations
   */
  createOrderTrackingData(orderId: string, customerId: number, couponLevel: number, items: any[], orderTotal: number): BounceBackOrderTracking {
    const trackingData = this.bounceBackUtils.createOrderTracking(orderId, customerId, couponLevel, items, orderTotal);
    
    // Log order tracking creation
    this.bounceBackUtils.logBounceBackTransaction('reward_used', {
      action: 'create_order_tracking',
      ...trackingData
    }, customerId);

    return trackingData;
  }

  /**
   * Validate order for bounceback usage
   */
  validateOrderForBounceBack(orderTotal: number, items: any[], storeId: string): { isValid: boolean; message?: string } {
    return this.bounceBackUtils.validateOrderRequirements(orderTotal, items, storeId);
  }

  // Helper methods

  /**
   * Check if coupon code is a BounceBack coupon
   */
  isBounceBackCoupon(couponCode: string): boolean {
    if (!couponCode) return false;
    const code = couponCode.toLowerCase().trim();
    // Only match exact "bounce back" or variations with proper spacing
    return code === 'bounce back' || code === 'bounceback' || code === 'bounce-back';
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number, currencyCode: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  }

  /**
   * Calculate discount amount
   */
  calculateDiscountAmount(subtotal: number, discountPercentage: number): number {
    return (subtotal * discountPercentage) / 100;
  }
}
